/* eslint-disable react-hooks/set-state-in-effect -- Effect synchronizes preview state with the external file/existingUrl props (HEIC conversion is async, so a derived value isn't viable). */
import heic2any from 'heic2any';
import { Camera, Image as ImageIcon, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isPdfReceiptUrl } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { Button } from '@splitfiesta/ui';

import { ReceiptPdfTile } from '@/components/expenses/receipt/ReceiptPdfTile';

import { isHeicFile } from '@/utils/imageOptimization';

/**
 * Detect if the device is running Android.
 * On Android 14+ with Chrome, react-dropzone's accept prop can disable camera access.
 * See: https://github.com/react-dropzone/react-dropzone/issues/1417
 */
const isAndroid = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
};

/** Allowed image extensions for validation */
const ALLOWED_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.webp', '.heic', '.heif'];

/** Check if a file has an allowed image extension */
const isAllowedImageFile = (file: File): boolean => {
  const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  return (
    ALLOWED_EXTENSIONS.includes(extension) || file.type.startsWith('image/')
  );
};

/**
 * Convert HEIC file to data URL for browser display
 * (Browsers can't display HEIC natively)
 */
const convertHeicToDataUrl = async (file: File): Promise<string> => {
  const convertedBlob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  });

  const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

interface ExpenseReceiptUploadProps {
  /** The currently selected file (deferred mode) */
  file: File | null;
  /** Existing receipt URL (edit mode) */
  existingUrl?: string | null;
  /** Called when a file is selected */
  onFileSelected: (file: File) => void;
  /** Called when file is removed */
  onFileRemoved: () => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * Specialized image upload component for expense receipts.
 *
 * Features:
 * - No cropping (receipts need full text preserved)
 * - Deferred mode only (upload happens after expense is created)
 * - Preview with remove button
 * - HEIC support for iPhone photos
 */
export const ExpenseReceiptUpload: React.FC<ExpenseReceiptUploadProps> = ({
  file,
  existingUrl,
  onFileSelected,
  onFileRemoved,
  disabled = false,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState<string | null>(existingUrl || null);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update preview when file changes
  useEffect(() => {
    if (!file) {
      // If no file and no existing URL, clear preview
      if (!existingUrl) {
        setPreview(null);
      } else {
        // Reset to existing URL if file was removed but we have an existing image
        setPreview(existingUrl);
      }
      return;
    }

    // Create preview for the selected file
    const createPreview = async () => {
      try {
        if (isHeicFile(file)) {
          setConverting(true);
          const dataUrl = await convertHeicToDataUrl(file);
          setPreview(dataUrl);
        } else {
          const reader = new FileReader();
          reader.onload = () => setPreview(reader.result as string);
          reader.readAsDataURL(file);
        }
      } catch {
        setError(t('common:upload.heicConversionError'));
      } finally {
        setConverting(false);
      }
    };

    createPreview();
  }, [file, existingUrl, t]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const selectedFile = acceptedFiles[0];
      setError(null);

      // On Android, we don't use accept prop (to enable camera), so validate manually
      if (!isAllowedImageFile(selectedFile)) {
        setError(t('common:upload.invalidFileType'));
        return;
      }

      // Check file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError(t('common:upload.fileSizeError'));
        return;
      }

      onFileSelected(selectedFile);
    },
    [onFileSelected, t]
  );

  // On Android 14+, the accept prop can disable camera access in Chrome
  // So we conditionally apply it only on non-Android devices
  // See: https://github.com/react-dropzone/react-dropzone/issues/1417
  const dropzoneAccept = useMemo(() => {
    if (isAndroid()) {
      // On Android: don't filter by accept to enable camera, validate manually instead
      return undefined;
    }
    return {
      'image/*': ALLOWED_EXTENSIONS,
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    maxFiles: 1,
    multiple: false,
    disabled: disabled || converting,
  });

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    setError(null);
    onFileRemoved();
  };

  const hasImage = preview !== null;

  return (
    <div data-testid={dataTestId}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {t('expenses:photo.receipt')}
        <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
          ({t('common:forms.optional')})
        </span>
      </label>

      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
          transition-colors duration-200
          ${isDragActive ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}
          ${disabled || converting ? 'opacity-50 cursor-not-allowed' : ''}
          ${error ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : ''}
        `}
        data-testid={dataTestId ? `${dataTestId}-dropzone` : undefined}
      >
        <input {...getInputProps()} />

        {hasImage ? (
          <div className="relative inline-block">
            {isPdfReceiptUrl(preview) ? (
              <div className="mx-auto h-32 w-24 overflow-hidden rounded-lg">
                <ReceiptPdfTile />
              </div>
            ) : (
              <img
                src={preview}
                alt={t('expenses:photo.receiptPreview')}
                className="mx-auto h-32 w-auto max-w-full object-contain rounded-lg"
                data-testid={dataTestId ? `${dataTestId}-image` : undefined}
              />
            )}
            {!disabled && !converting && (
              <Button
                variant="danger"
                size="sm"
                type="button"
                onClick={handleRemove}
                aria-label={t('expenses:photo.removeReceipt')}
                className="absolute top-0 right-0 -mt-2 -mr-2 rounded-full p-1 w-6 h-6 min-w-0"
                data-testid={dataTestId ? `${dataTestId}-remove` : undefined}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2 py-2">
            <div className="flex justify-center">
              <div className="flex items-center space-x-2 text-gray-400 dark:text-gray-500">
                <Camera className="h-8 w-8" />
                <ImageIcon className="h-6 w-6" />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {isDragActive ? (
                <p>{t('common:upload.dropImageHere')}</p>
              ) : (
                <>
                  <p className="font-medium">
                    {t('expenses:photo.addReceipt')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 whitespace-pre-line">
                    {t('common:upload.supportedFormatsReceipt')}
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {converting && (
          <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 rounded-lg">
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('common:upload.converting')}
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};
